/**
 * Quarterly plan-switch offer (experiment `plan-switch-nudge-v1`).
 *
 * The patient arrives from a renewal-anchored email/SMS link
 * (`/plan-offer/<token>` → magic link → `/app/account?planOffer=<id>`), so
 * the nudge id in the URL is the only handle the modal needs. Prices come
 * from the backend's enrollment snapshot — the same numbers the message
 * quoted — while availability is re-derived from Stripe per request.
 *
 * See backend/app/api/v1/plan_offer.py.
 */

import { useMutation, useQuery } from '@tanstack/react-query';

import { apiFetch } from './client';

export interface PlanOffer {
  nudge_id: string;
  subscription_id: string;
  product_id: string;
  product_title: string;
  monthly_renewal_cents: number;
  quarterly_renewal_cents: number;
  quarterly_per_month_cents: number;
  savings_per_month_cents: number;
  savings_per_quarter_cents: number;
  next_renewal_date: string | null;
  first_shipment_containers: number | null;
  container_noun: string | null;
  experiment_key: string | null;
  variant_key: string | null;
  /** null = live. Otherwise why the offer can't be taken any more
   *  ("cancelling" | "paused" | "already_switched" | "not_monthly" | …). */
  unavailable_reason: string | null;
  switched: boolean;
}

export function usePlanOffer(nudgeId: string | null) {
  return useQuery({
    queryKey: ['me', 'plan-offer', nudgeId],
    queryFn: () =>
      apiFetch<PlanOffer>(
        `/me/plan-offer?nudge=${encodeURIComponent(nudgeId as string)}`,
      ),
    enabled: Boolean(nudgeId),
    // Availability is re-checked server-side per request; don't serve a
    // cached "live" offer to a patient who cancelled in another tab.
    staleTime: 0,
    retry: false,
  });
}

/** Funnel stamps on the enrollment row (modal_viewed / dismissed). The
 *  switch itself is stamped server-side from change-plan, for both arms. */
export function usePlanOfferEvent() {
  return useMutation({
    mutationFn: ({
      nudgeId,
      eventType,
    }: {
      nudgeId: string;
      eventType: 'modal_viewed' | 'dismissed';
    }) =>
      apiFetch<{ ok: boolean }>(`/me/plan-offer/${nudgeId}/events`, {
        method: 'POST',
        body: JSON.stringify({ event_type: eventType }),
      }),
  });
}
